import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ToggleSwitch } from 'primeng/toggleswitch';

import { differenceInCalendarDays, format } from 'date-fns';
import { de } from 'date-fns/locale';

import { TranslationModule } from '@core/translation/translation.module';
import { BcmService } from '@modules/bcm/bcm.service';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';

import { BookingV2Facade } from '../../services/booking-v2.facade';
import { bookingV2SummaryRelationsLabelKey } from '../../utils/booking-v2-relation-label.util';
import { AdvancedStepComponent } from '../advanced-step/advanced-step.component';
import { BookingV2OrderLinkCardComponent } from '../../components/booking-v2-order-link-card/booking-v2-order-link-card.component';
import { BookingV2DimensionsComponent } from '../../components/dimensions/booking-v2-dimensions.component';

@Component({
    selector: 'booking-v2-summary-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, ToggleSwitch, TranslationModule, U2bIconButtonComponent, U2bIndicatorCardComponent, AdvancedStepComponent, BookingV2OrderLinkCardComponent, BookingV2DimensionsComponent],
    templateUrl: './summary-step.component.html',
    host: { class: 'block' },
})
export class SummaryStepComponent {

    // Public so the template can call goToStep() for the per-section edit links.
    readonly facade = inject(BookingV2Facade);
    private readonly bcm = inject(BcmService);

    summaryRelationsKey(): string {
        const tenant = this.bcm.tenant;
        return bookingV2SummaryRelationsLabelKey(tenant.registerType, tenant.productType);
    }

    readonly isEditMode = this.facade.isEditMode;
    readonly contractIntent = this.facade.contractIntent;

    readonly bookingType = this.facade.bookingType;
    readonly dateRange = this.facade.dateRange;
    readonly berthOccupancies = this.facade.berthOccupancies;
    readonly selectedBoat = this.facade.selectedBoat;
    readonly selectedPerson = this.facade.selectedPerson;
    readonly selectedRelation = this.facade.selectedRelation;
    readonly bookingNotes = this.facade.bookingNotes;

    // Nights between arrival and departure (only for a closed-end range). The unit
    // label follows the tenant's configured stay unit (facade.durationUnitLabelKey).
    readonly dayCount = computed(() => {
        const { from, to, openEnd } = this.dateRange();
        return from && to && !openEnd ? Math.max(differenceInCalendarDays(to, from), 0) : 0;
    });

    // Show the read-only advanced tabs (Reisende / Strom / Schlüssel / Zus.
    // Leistungen / Dokumente) when advanced mode was used or any advanced data
    // was captured (the data persists even if advanced mode is toggled off).
    readonly hasAdvancedData = computed(() =>
        this.facade.advancedMode()
        || this.facade.travelers().length > 0
        || this.facade.electricMeterAssignments().length > 0
        || this.facade.keys().length > 0
        || this.facade.services().length > 0
        || this.facade.documents().length > 0,
    );

    // Long, German-localized date+time — mirrors the date-range step's summary.
    formatDate(date: Date | null): string {
        return date && !isNaN(date.getTime())
            ? format(date, "EEEE, dd. MMMM yyyy, HH:mm 'Uhr'", { locale: de })
            : '';
    }
}
